/**
 * Contacts API Service
 * Contact management related API calls
 */

import { api } from '@/shared/api/apiClient';
import { API_ROUTES, buildUrl } from '@/config/apiRoutes';
import type { ContactBan } from '@/utils/contactBan';
import type { PaginatedResponse, SearchParams } from '@/shared/api/types';
import type {
    Contact, CreateContactDTO, UpdateContactDTO, ContactListParams, LeadOverride, RelationshipType,
} from '@/types/entities';

// Re-export types for convenience
export type { Contact, CreateContactDTO, UpdateContactDTO, RelationshipType };

export interface ContactRelationship {
    id: number;
    contact_id: number;
    related_contact_id: number;
    relationship_type_id: number;
    created_at?: string;
    updated_at?: string;
}

export interface BatchRelationshipCreate {
    contact_id: number;
    related_contact_ids: number[];
    relationship_types: Record<string, number>;
    user_id?: number;
}

// Extended params with SearchParams
export interface ContactsListParams extends SearchParams, ContactListParams {}

/**
 * `GET /contacts` pages with `skip` / `limit` (default 100, max 1000), not
 * `page` / `per_page`; sending the latter silently returned the first 100
 * rows. Translate so callers can use either spelling.
 */
function toContactsQuery(params: ContactsListParams | undefined) {
    if (!params) return undefined;
    const { page, per_page, ...rest } = params;
    const limit = rest.limit ?? per_page;
    const skip = rest.skip ?? (page && limit ? (page - 1) * limit : undefined);
    // Spread into a plain object: buildUrl wants an indexable record,
    // which an interface type is not.
    return { ...rest, limit, skip };
}

// ===========================================
// Contacts Service
// ===========================================
export const contactsApi = {
    /**
     * Get paginated contacts list
     */
    getContacts: async (params?: ContactsListParams): Promise<PaginatedResponse<Contact>> => {
        const url = buildUrl(API_ROUTES.admin.contacts.base, toContactsQuery(params));
        return api.get<PaginatedResponse<Contact>>(url);
    },

    /**
     * Get single contact by ID
     */
    getContactById: async (id: number | string): Promise<Contact> => {
        return api.get<Contact>(API_ROUTES.admin.contacts.getById(id));
    },

    /**
     * Create new contact
     */
    createContact: async (data: CreateContactDTO): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.create, data);
    },

    /**
     * Update existing contact (PATCH - partial update)
     */
    updateContact: async (id: number | string, data: UpdateContactDTO): Promise<Contact> => {
        return api.patch<Contact>(API_ROUTES.admin.contacts.update(id), data);
    },

    /**
     * Delete contact (soft delete)
     */
    deleteContact: async (id: number | string): Promise<void> => {
        return api.delete(API_ROUTES.admin.contacts.delete(id));
    },

    /**
     * Restore deleted contact
     */
    restoreContact: async (id: number | string): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.restore(id));
    },

    /**
     * Ban contact: nobody calls, texts or emails them until they're
     * unbanned. `reason` is shown wherever the ban is.
     */
    banContact: async (id: number | string, reason?: string | null): Promise<Contact> => {
        const note = reason?.trim();
        const query = note ? `?${new URLSearchParams({ reason: note })}` : '';
        return api.post<Contact>(`${API_ROUTES.admin.contacts.ban(id)}${query}`);
    },

    /**
     * Unban contact
     */
    unbanContact: async (id: number | string): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.unban(id));
    },

    /**
     * Get relationship types
     */
    getRelationshipTypes: async (): Promise<RelationshipType[]> => {
        return api.get<RelationshipType[]>(API_ROUTES.admin.relationshipTypes);
    },

    /**
     * Get relationships for a contact
     */
    getContactRelationships: async (contactId: number | string): Promise<ContactRelationship[]> => {
        return api.get<ContactRelationship[]>(API_ROUTES.admin.contacts.relationships(contactId));
    },

    /**
     * Create multiple contact relationships
     */
    createBatchRelationships: async (data: BatchRelationshipCreate): Promise<ContactRelationship[]> => {
        return api.post<ContactRelationship[]>(
            API_ROUTES.admin.contacts.relationshipsBatch(data.contact_id),
            data
        );
    },

    /**
     * Delete a contact relationship
     */
    deleteRelationship: async (
        contactId: number | string,
        relationshipId: number | string
    ): Promise<void> => {
        return api.delete(API_ROUTES.admin.contacts.relationship(contactId, relationshipId));
    },
};

/** Re-analyze a contact's chat: runs on the server; the result lands on the contact. */
export interface LeadAssessmentStarted {
    status: 'running';
    contact_id: number;
    previous_assessed_at: string | null;
}

/** Lead state, re-analysis and brokerage logos (backend lead_state / lead_detection / brokerage_logos). */
export const leadsApi = {
    /** Lead or Not a lead; `null` hands the decision back to the automatic rules. */
    setLeadOverride: (id: number | string, lead_override: LeadOverride | null): Promise<Contact> =>
        api.patch<Contact>(API_ROUTES.admin.contacts.update(id), { lead_override }),

    reassess: (id: number | string): Promise<LeadAssessmentStarted> =>
        api.post<LeadAssessmentStarted>(API_ROUTES.admin.contacts.leadAssessment(id), {}),

    uploadCompanyLogo: (companyId: number | string, file: File): Promise<Contact> => {
        const form = new FormData();
        form.append('file', file);
        return api.post<Contact>(API_ROUTES.admin.companies.logo(companyId), form);
    },

    fetchCompanyLogo: (companyId: number | string): Promise<Contact> =>
        api.post<Contact>(API_ROUTES.admin.companies.fetchLogo(companyId), {}),

    removeCompanyLogo: (companyId: number | string): Promise<Contact> =>
        api.delete<Contact>(API_ROUTES.admin.companies.logo(companyId)),
};

/** Email the agent's business card (the card turning over, Save my contact, the .vcf). */
/** Is this number a banned contact's? `null` when not, or when the check fails (the send is refused server-side anyway). */
export const contactBanApi = {
    forPhone: async (phone: string): Promise<(ContactBan & { banned: true }) | null> => {
        try {
            const status = await api.get<{ banned: boolean } & Partial<ContactBan>>(
                '/sms/contact-ban-status', { phone },
            );
            return status?.banned ? { ...(status as ContactBan), banned: true } : null;
        } catch {
            return null;
        }
    },
};

export const businessCardApi = {
    /** `to` defaults to the contact's own email; `message` is a note above the card. */
    email: (contactId: number | string, body: { to?: string; message?: string } = {}): Promise<{ to: string }> =>
        api.post<{ to: string }>(API_ROUTES.admin.contacts.emailBusinessCard(contactId), body),
};

export default contactsApi;
